/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, createContext } from "react";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import { CAPTCHA_INIT_PAGES } from "Static/constant";
import BreadCrumbComponent from "Components/common/breadcrumb";
import { ReactComponent as RightArrow } from "Assets/svg/rightArrow.svg";
import { NavLink, withRouter, useLocation } from "react-router-dom";
import SettingsRouting from "Routes/SettingsRouting";
import { captchaStoreValue } from "Stores/captcha";
import Utils from "Utils/utils";
import PricingDiscountBanner from "Components/common/PricingDiscountBanner";
import UpgradeBadge from "Components/Paywall/UpgradeBadge";
import PaywallUtils from "Utils/PaywallUtils";

// Filter the settings tab tree by a free-text query. Group items are kept when
// their own name matches (whole group shown, expanded) OR when any child name
// matches (only matching children shown, group expanded). Leaves are kept on
// direct name match. Empty query returns the input unchanged.
const filterSettingsTabs = (tabs, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return tabs;
  const matches = (s) => (s || "").toLowerCase().includes(q);
  const out = [];
  for (const t of tabs || []) {
    const subMenu = Array.isArray(t.subMenu) ? t.subMenu : [];
    if (subMenu.length) {
      if (matches(t.name)) {
        out.push({ ...t, showSubMenu: true });
      } else {
        const matchingChildren = subMenu.filter((c) => matches(c.name));
        if (matchingChildren.length) {
          out.push({ ...t, showSubMenu: true, subMenu: matchingChildren });
        }
      }
    } else if (matches(t.name)) {
      out.push(t);
    }
  }
  return out;
};

const settingsWithSubMenu = {
  groups: [
    "deepResearch",
    "contactSettings",
    "outreach",
    "deliverability",
    "integrations",
    "callingCalendar",
    "billing"
  ],
  pages: [
    "dialer",
    "schedules",
    "imports",
    "triggers",
    "stages",
    "customUrl",
    "customFields",
    "emailSettings",
    "billing",
    "csvList",
    "creditUsage"
  ]
};

const SettingContext = createContext({});

const SettingsComponent = observer(props => {
  const { location = "" } = props || {};
  const {
    team = "",
    isTeamOwner = false,
    calendarEnabled = false
  } = userDetail?.userInfo || {};

  const {
    calendarIntegrationEnabled = false,
    inFreePlan = false,
    teamsEnabled = false,
    customFieldsEnabled = false,
    autoBccEnabled = false,
    contactStagesEnabled = false,
    donotContactEnabled = false,
    brandedUrlEnabled = false,
    dialerEnabled = false,
    triggersEnabled = false,
    planName = "",
    domainHealthCenterEnabled = false
  } = userDetail?.userFeatureFlag || {};

  const displayPlanName = (isHierachy = false) => {
    return PaywallUtils.getPlanName(planName, inFreePlan, isHierachy, "");
  };

  let settingsTabList = [
    // ── Top-level personal/admin items ──
    {
      name: "My Profile",
      id: "general",
      path: "/settings/general"
    },
    { name: "Connected Accounts", id: "account", path: "/settings/account" },
    {
      name: "Users & Teams",
      id: "team",
      upgradePlan: teamsEnabled ? "" : displayPlanName(true),
      path: "/settings/team"
    },
    // ── Grouped settings ──
    // Deep Research settings group is gated by team.enableSequenceDeepResearch.
    // Hidden entirely (group + sub-pages) when feature is off for the team.
    ...(Utils.isSequenceDRFeatureEnabled()
      ? [
          {
            name: "Deep Research",
            id: "deepResearch",
            subMenu: [
              {
                name: "Seller Profiles",
                id: "sellerProfiles",
                path: "/settings/seller-profiles"
              },
              {
                name: "Usage",
                id: "deepResearchUsage",
                path: "/settings/deep-research-usage"
              }
            ]
          }
        ]
      : []),
    {
      name: "Contacts",
      id: "contactSettings",
      subMenu: [
        {
          name: "Contact Stages",
          id: "stages",
          upgradePlan: contactStagesEnabled ? "" : displayPlanName(false),
          path: "/settings/stages"
        },
        {
          name: "Custom Fields",
          id: "customFields",
          upgradePlan: customFieldsEnabled ? "" : displayPlanName(false),
          path: "/settings/customFields"
        },
        {
          name: "Do Not Contact Domains",
          id: "doNotContact",
          upgradePlan: donotContactEnabled ? "" : displayPlanName(false),
          path: "/settings/doNotContact",
          showBadgeIcon: false
        },
        { name: "Imports/Exports", id: "imports", path: "/settings/imports" }
      ]
    },
    {
      name: "Outreach",
      id: "outreach",
      subMenu: [
        { name: "Schedules", id: "schedules", path: "/settings/schedules" },
        {
          name: "Triggers",
          id: "triggers",
          path: "/settings/triggers",
          upgradePlan: triggersEnabled ? "" : displayPlanName(false)
        },
        {
          name: "Auto Bcc",
          id: "autoBcc",
          upgradePlan: autoBccEnabled ? "" : displayPlanName(false),
          path: "/settings/autoBcc"
        }
      ]
    },
    {
      name: "Deliverability",
      id: "deliverability",
      subMenu: [
        {
          name: "Email Settings",
          id: "emailSettings",
          path: "/settings/emailSettings",
          upgradePlan: "basic"
        },
        {
          name: "Custom URL",
          id: "customUrl",
          path: "/settings/customUrl",
          upgradePlan: brandedUrlEnabled ? "" : displayPlanName(false)
        },
        {
          name: "Domain Health Center",
          id: "domainHealthCenter",
          path: "/settings/domain-health-center",
          upgradePlan: domainHealthCenterEnabled ? "" : displayPlanName(false),
          showBadgeIcon: false
        },
        {
          name: "Unsubscribe",
          id: "unsubscribe",
          upgradePlan: "",
          path: "/settings/unsubscribe"
        }
      ]
    },
    {
      name: "Integrations",
      id: "integrations",
      subMenu: [
        {
          name: "CRM",
          id: "crm",
          path: "/settings/crm",
          upgradePlan: "pro"
        },
        {
          name: "Website Tracking",
          id: "websiteTracking",
          path: "/settings/website-tracking",
          hasBetaBadge: true
        }
      ]
    },
    {
      name: "Calling & Calendar",
      id: "callingCalendar",
      subMenu: [
        {
          name: "Dialer",
          id: "dialer",
          path: "/settings/dialer",
          upgradePlan: dialerEnabled ? "" : displayPlanName(false)
        },
        ...(Utils.isAdmin() ? [{ name: "Call Credit Usage", id: "callCreditUsage", path: "/settings/callCreditUsage" }] : []),
        {
          name: "Calendar",
          id: "calendar",
          path: "/settings/calendar",
          upgradePlan: calendarEnabled ? "" : displayPlanName(false)
        }
      ]
    },
    {
      name: "Billing",
      id: "billing",
      subMenu: [
        { name: "Billing", id: "billing", path: "/settings/billing" },
        {
          name: "Credit Usage",
          id: "creditUsage",
          path: "/settings/creditUsage"
        }
      ]
    }
  ];

  const defaultBreadValue = [
    { name: "Settings", link: "/settings/general/profile" }
  ];

  const [breadcrumbArr, setBreadCrumbArr] = useState(defaultBreadValue);
  const [productDiscount, setProductDiscount] = useState(
    Utils.showProductDiscountBanner()
  );
  const [settingsTabArr, setSettingsTabArr] = useState(settingsTabList);
  const [searchQuery, setSearchQuery] = useState("");
  const visibleTabs = filterSettingsTabs(settingsTabArr, searchQuery);
  const [isTeamPage, setIsTeamPage] = useState(false);

  const captchaInitialize = () => {
    captchaStoreValue.setCaptcha(
      CAPTCHA_INIT_PAGES.includes(location?.pathname)
    );
  };

  const getUpdateBreadcrumb = () => {
    const pathName = location?.pathname || "";
    setIsTeamPage(false);
    if (pathName.indexOf("/settings/crm/salesforce") !== -1) {
      updateBreadCrumb("Integrations - Salesforce Settings");
    } else if (pathName.indexOf("/settings/crm/zoho") !== -1) {
      updateBreadCrumb("Integrations - Zoho settings");
    } else if (pathName.indexOf("/settings/crm/hubspot") !== -1) {
      updateBreadCrumb("Integrations - Hubspot Settings");
    } else if (pathName.indexOf("/settings/crm/pipedrive") !== -1) {
      updateBreadCrumb("Integrations - Pipedrive Settings");
    } else if (pathName.indexOf("/settings/crm/slack") !== -1) {
      updateBreadCrumb("Integrations - Slack Settings");
    } else if (pathName.indexOf("/settings/crm") !== -1) {
      updateBreadCrumb("Integrations");
    } else if (pathName.indexOf("/settings/schedules") !== -1) {
      updateBreadCrumb("Schedules");
    } else if (pathName.indexOf("/settings/general/profile") !== -1) {
      updateBreadCrumb("My Profile");
    } else if (pathName.indexOf("/settings/general/email-reports") !== -1) {
      updateBreadCrumb("Email Reports");
    } else if (pathName.indexOf("/settings/autoBcc") !== -1) {
      updateBreadCrumb("Auto Bcc");
    } else if (pathName.indexOf("/settings/billing") !== -1) {
      updateBreadCrumb("Billing");
    } else if (pathName.indexOf("/settings/creditUsage") !== -1) {
      updateBreadCrumb("Credit Usage");
    } else if (team && pathName.indexOf("/settings/website-tracking") !== -1) {
      updateBreadCrumb("Website Tracking", true);
    } else if (pathName.indexOf("/settings/domain-health-center") !== -1) {
      updateBreadCrumb("Domain Health Center");
    } else if (pathName.indexOf("/settings/unsubscribe") !== -1) {
      updateBreadCrumb("Unsubscribe");
    } else if (pathName.indexOf("/settings/callCreditUsage") !== -1) {
      updateBreadCrumb("Call Credit Usage");
    } else if (pathName.indexOf("/settings/dialer") !== -1) {
      updateBreadCrumb("Dialer");
    } else if (pathName.indexOf("/settings/customUrl") !== -1) {
      updateBreadCrumb("Custom Url");
    } else if (pathName.indexOf("/settings/imports") !== -1) {
      updateBreadCrumb("Imports");
    } else if (pathName.indexOf("/settings/bulk-actions") !== -1) {
      updateBreadCrumb("Bulk Actions");
    } else if (pathName.indexOf("/settings/customFields") !== -1) {
      updateBreadCrumb("Custom Fields");
    } else if (pathName.indexOf("/settings/emailSettings") !== -1) {
      updateBreadCrumb("Email Settings");
    } else if (team && pathName.indexOf("/settings/triggers") !== -1) {
      updateBreadCrumb("Triggers");
    } else if (
      calendarEnabled &&
      calendarIntegrationEnabled &&
      pathName.indexOf("/settings/calendar/events") !== -1
    ) {
      updateBreadCrumb("Calendar Events");
    } else if (
      calendarEnabled &&
      calendarIntegrationEnabled &&
      pathName.indexOf("/settings/calendar/configuration") !== -1
    ) {
      updateBreadCrumb("Calendar Configuration");
    } else if (pathName.indexOf("/settings/seller-profiles/new") !== -1) {
      updateBreadCrumb("Seller Profiles - New");
    } else if (pathName.match(/\/settings\/seller-profiles\/[^/]+/)) {
      updateBreadCrumb("Seller Profiles - Edit");
    } else if (pathName.indexOf("/settings/seller-profiles") !== -1) {
      updateBreadCrumb("Seller Profiles");
    } else if (pathName.indexOf("/settings/deep-research-usage") !== -1) {
      updateBreadCrumb("Deep Research Usage");
    } else if (pathName.indexOf("/settings/stages") !== -1) {
      updateBreadCrumb("Contact Stages");
    } else if (team && pathName.indexOf("/settings/doNotContact") !== -1) {
      updateBreadCrumb("Do Not Contact Domains");
    } else if (
      (team || isTeamOwner) &&
      pathName.indexOf("/settings/team") !== -1
    ) {
      updateBreadCrumb("Users & Teams");
      setIsTeamPage(true);
    } else {
      updateBreadCrumb("Connected Accounts");
    }
  };

  const updateBreadCrumb = (name, hasBetaBadge = false) => {
    let settingPath = defaultBreadValue;
    settingPath.push({
      name,
      link: location?.pathname || "/settings/general/profile",
      hasBetaBadge
    });
    settingPath?.length && setBreadCrumbArr([...settingPath]);
  };

  const showTabsubMenu = id => {
    const tempArr = Utils.createObjWithoutRef(settingsTabList);
    if (tempArr) {
      tempArr &&
        tempArr.map(value => {
          value?.id?.toLowerCase() === id?.toLowerCase() &&
            (value.showSubMenu =
              typeof value.showSubMenu !== undefined
                ? !value.showSubMenu
                : true);
        });
      setSettingsTabArr(tempArr);
    }
  };

  const checkUrlAndShowSubMenu = () => {
    const settingsTabArr = Utils.createObjWithoutRef(settingsTabList);
    if (settingsTabArr?.length) {
      settingsTabArr.map(value => {
        if (value?.id && settingsWithSubMenu.groups.includes(value?.id)) {
          value.showSubMenu = true;
        }
      });
      setSettingsTabArr(settingsTabArr);
    }
  };

  useEffect(() => {
    captchaInitialize();
    getUpdateBreadcrumb();
  }, [props.location.pathname]);

  useEffect(() => {
    setProductDiscount(Utils.showProductDiscountBanner());
  }, [Utils.showProductDiscountBanner()]);

  useEffect(() => {
    document.body.classList.add("settingsPage");
    checkUrlAndShowSubMenu();
    return () => {
      document.body.classList.remove("settingsPage");
    };
  }, []);

  return (
    <div className={`settingsPage pageClass`}>
      <div className="settingsCont pageCont">
        <section className="headerSection">
          <BreadCrumbComponent breadCrumbValue={breadcrumbArr} />
        </section>
        {!productDiscount && <PricingDiscountBanner />}
        <section
          className={`settingSection ${
            !productDiscount ? "productDiscount" : ""
          }`}
        >
          <div className="leftSection">
            <div className="tabContainer">
              <div className="settingsSearchWrap">
                <i className="material-icons settingsSearchIcon">search</i>
                <input
                  type="text"
                  className="settingsSearchInput"
                  placeholder="Search settings"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="settingsSearchClear"
                    onClick={() => setSearchQuery("")}
                    title="Clear search"
                  >
                    <i className="material-icons">close</i>
                  </button>
                )}
              </div>
              {visibleTabs?.length ? (
                visibleTabs.map(item => {
                  const {
                    id = "",
                    subMenu = [],
                    showSubMenu = false
                  } = item || {};
                  return subMenu?.length ? (
                    <TabWithSubMenu
                      key={`settingsTabSubmenu${id}`}
                      {...item}
                      showTabsubMenu={showTabsubMenu}
                      showSubMenu={showSubMenu}
                      team={team}
                      calendarEnabled={calendarEnabled}
                      calendarIntegrationEnabled={calendarIntegrationEnabled}
                    />
                  ) : (
                    (id !== "team" ||
                      ((team || isTeamOwner) && id === "team")) && (
                      <Tab key={`settingsUrlPath${id}`} {...item} />
                    )
                  );
                })
              ) : searchQuery ? (
                <div className="settingsSearchEmpty">
                  No settings match "<strong>{searchQuery}</strong>"
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className={`rightSection  ${isTeamPage ? "isTeamPage" : ""}`}>
            <SettingContext.Provider
              value={{
                updateBreadCrumb,
                getUpdateBreadcrumb,
                breadcrumbArr,
                setBreadCrumbArr
              }}
            >
              <SettingsRouting />
            </SettingContext.Provider>
          </div>
        </section>
      </div>
    </div>
  );
});

const Tab = observer(props => {
  let location = useLocation();

  const {
    id = "",
    name = "",
    upgradePlan = "",
    path = "",
    showBadgeIcon = true
  } = props || {};

  const { inFreePlan = false, planName = "" } =
    userDetail?.userFeatureFlag || {};

  const multipleRouteConditions = {
    calendar: "/settings/calendar",
    stages: "/settings/stages",
    dialer: "/settings/dialer",
    general: "settings/general",
    crm: "/settings/crm",
    account: "/settings/account",
    imports: "/settings/imports"
  };

  const checkActiveTabForMultipleRoutes = () => {
    const path = multipleRouteConditions[id];
    return path && location?.pathname.includes(path);
  };

  return (
    <NavLink
      to={path}
      exact={true}
      activeClassName="selected"
      className={`tabValue ${
        ["calendar", "stages", "dialer", "general", "crm", "account"].includes(
          id
        ) && checkActiveTabForMultipleRoutes()
          ? "selected"
          : ""
      }`}
    >
      <div className="navWrapper">
        <div id={id} className="tab">
          {name}
        </div>
        {planName?.toLowerCase() === "basic" &&
          planName?.toLowerCase() !== upgradePlan && (
            <UpgradeBadge
              plan={upgradePlan && `${upgradePlan}Plan`}
              badgeSize={"md"}
              showBadgeIcon={showBadgeIcon}
            />
          )}
      </div>
      <span className="rightArrow">
        <RightArrow />
      </span>
    </NavLink>
  );
});

const TabWithSubMenu = props => {
  const {
    id = "",
    name = "",
    subMenu = "",
    showTabsubMenu = "",
    showSubMenu = "",
    team = false,
    calendarEnabled = false,
    calendarIntegrationEnabled = false
  } = props || {};

  return subMenu?.length ? (
    <div
      className={`tabValue subMenu ${showSubMenu ? "openSubMenu" : ""}`}
      id={id}
    >
      <div id={id} className="tab" onClick={e => showTabsubMenu(id)}>
        <span className="tabText"> {name}</span>
        <i className="material-icons subMenuDownArrow">keyboard_arrow_down</i>
      </div>
      {showSubMenu && (
        <div className="subMenuList">
          {subMenu.map(item => {
            // Calendar used to be suppressed entirely when the feature flag
            // was off — but every other paywalled item just shows an upgrade
            // badge. Treat Calendar the same so it stays discoverable.
            const isNonAdmin =
              !team &&
              ["triggers", "stages", "doNotContact"].includes(item?.id);
            return !isNonAdmin ? (
              <Tab
                key={`settingSubmenuAccountSettings${item?.id}`}
                {...item}
              />
            ) : (
              ""
            );
          })}
        </div>
      )}
    </div>
  ) : (
    ""
  );
};

export default withRouter(SettingsComponent);
export { SettingsComponent, SettingContext };
